import { useState, useRef, useEffect, useCallback } from "react";
import { Search, Music, Ticket, Loader2, MapPin, Star, Sparkles } from "lucide-react";
import { debounce } from "../utils/debounce";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EventArtistSearch = ({ onSelect, placeholder = "Search events, artists..." }) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState({ events: [], artists: [], vipArtists: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced search function
  const searchEvents = useCallback(
    debounce(async (searchQuery) => {
      if (!searchQuery || searchQuery.length < 2) {
        setResults({ events: [], artists: [], vipArtists: [] });
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Search events, artists, AND VIP artist suggestions in parallel
        const [eventsRes, artistRes, vipRes] = await Promise.all([
          fetch(`${API_URL}/api/events?search=${encodeURIComponent(searchQuery)}&limit=5`),
          fetch(`${API_URL}/api/events?artist=${encodeURIComponent(searchQuery)}&limit=5`),
          fetch(`${API_URL}/api/events/artist-suggestions?q=${encodeURIComponent(searchQuery)}`)
        ]);

        const eventsData = await eventsRes.json();
        const artistData = await artistRes.json();
        const vipData = await vipRes.json();

        // Filter events that match the query in name
        const matchingEvents = (eventsData.events || []).filter(event =>
          event.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          event.artists?.some(a => a.toLowerCase().includes(searchQuery.toLowerCase()))
        );

        // Get unique artists from artist search results
        const artistSet = new Set();
        (artistData.events || []).forEach(event => {
          event.artists?.forEach(artist => {
            if (artist.toLowerCase().includes(searchQuery.toLowerCase())) {
              artistSet.add(artist);
            }
          });
        });

        // Get VIP artist suggestions
        const vipArtists = vipData.suggestions || [];

        setResults({
          events: matchingEvents.slice(0, 5),
          artists: Array.from(artistSet).slice(0, 3),
          vipArtists: vipArtists.slice(0, 5)
        });
        setIsOpen(true);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  // Trigger search when query changes
  useEffect(() => {
    if (query.length >= 2) {
      searchEvents(query);
    } else {
      setResults({ events: [], artists: [], vipArtists: [] });
      setIsOpen(false);
    }
  }, [query, searchEvents]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Calculate total items for keyboard nav
  const totalItems = results.vipArtists.length + results.events.length + results.artists.length;

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || totalItems === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, totalItems - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        handleSelectByIndex(highlightedIndex);
        break;
      case "Escape":
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectByIndex = (index) => {
    // VIP Artists come first
    if (index < results.vipArtists.length) {
      handleSelectVipArtist(results.vipArtists[index]);
    } else if (index < results.vipArtists.length + results.events.length) {
      // Then events
      const eventIndex = index - results.vipArtists.length;
      handleSelectEvent(results.events[eventIndex]);
    } else {
      // Then regular artists
      const artistIndex = index - results.vipArtists.length - results.events.length;
      handleSelectArtist(results.artists[artistIndex]);
    }
  };

  const handleSelectEvent = (event) => {
    setQuery(event.name);
    setIsOpen(false);
    onSelect({ type: "event", data: event });
  };

  const handleSelectArtist = (artist) => {
    setQuery(artist);
    setIsOpen(false);
    onSelect({ type: "artist", data: artist });
  };

  const handleSelectVipArtist = (vipArtist) => {
    setQuery(vipArtist.name);
    setIsOpen(false);
    onSelect({ type: "artist", data: vipArtist.name });
  };

  const hasResults = results.events.length > 0 || results.artists.length > 0 || results.vipArtists.length > 0;

  return (
    <div ref={wrapperRef} className="relative flex-1" style={{ overflow: 'visible' }}>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => hasResults && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full h-14 pl-12 pr-4 bg-transparent text-foreground placeholder:text-muted-foreground text-lg focus:outline-none"
          data-testid="event-search-input"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
        {isLoading && (
          <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary animate-spin" />
        )}
      </div>

      {isOpen && hasResults && (
        <div 
          className="autocomplete-dropdown absolute left-0 right-0 mt-2 glass-strong rounded-xl shadow-2xl border border-border/50 overflow-hidden"
          style={{ zIndex: 9999 }}
          data-dropdown="true"
        >
          <div className="max-h-96 overflow-y-auto">
            {/* VIP Artists Section - Show First */}
            {results.vipArtists.length > 0 && (
              <div>
                <div className="px-4 py-2 text-xs text-yellow-400 uppercase tracking-wider bg-yellow-400/10 sticky top-0 flex items-center gap-2">
                  <Sparkles className="w-3 h-3" />
                  XTrip Legends
                </div>
                {results.vipArtists.map((vipArtist, index) => {
                  const isHighlighted = index === highlightedIndex;
                  return (
                    <button
                      key={vipArtist.name}
                      onClick={() => handleSelectVipArtist(vipArtist)}
                      className={`w-full px-4 py-3 flex items-center gap-3 transition-colors text-left ${
                        isHighlighted ? "bg-yellow-400/20" : "hover:bg-muted/50"
                      }`}
                    >
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-yellow-400/20 to-primary/20 ring-2 ring-yellow-400/50">
                        {vipArtist.image_url ? (
                          <img src={vipArtist.image_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Star className="w-4 h-4 text-yellow-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-foreground flex items-center gap-2">
                          {vipArtist.name}
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-400/20 text-yellow-400 font-bold">
                            LEGEND
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {vipArtist.event_count > 0 
                            ? `${vipArtist.event_count} upcoming event${vipArtist.event_count > 1 ? 's' : ''}`
                            : 'No upcoming events'
                          }
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Events Section */}
            {results.events.length > 0 && (
              <div>
                <div className="px-4 py-2 text-xs text-muted-foreground uppercase tracking-wider bg-muted/30 sticky top-0 flex items-center gap-2">
                  <Ticket className="w-3 h-3" />
                  Events
                </div>
                {results.events.map((event, index) => {
                  const globalIndex = results.vipArtists.length + index;
                  const isHighlighted = globalIndex === highlightedIndex;
                  return (
                    <button
                      key={event.event_id}
                      onClick={() => handleSelectEvent(event)}
                      className={`w-full px-4 py-3 flex items-center gap-3 transition-colors text-left ${
                        isHighlighted ? "bg-primary/20" : "hover:bg-muted/50"
                      }`}
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                        {event.image_url ? (
                          <img src={event.image_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Ticket className="w-5 h-5 text-primary" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-foreground truncate">{event.name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{event.venue_city}</span>
                          <span className="text-primary">•</span>
                          <span className="text-primary font-mono">£{event.ticket_price}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Artists Section */}
            {results.artists.length > 0 && (
              <div>
                <div className="px-4 py-2 text-xs text-muted-foreground uppercase tracking-wider bg-muted/30 sticky top-0 flex items-center gap-2">
                  <Music className="w-3 h-3" />
                  Artists
                </div>
                {results.artists.map((artist, index) => {
                  const globalIndex = results.vipArtists.length + results.events.length + index;
                  const isHighlighted = globalIndex === highlightedIndex;
                  return (
                    <button
                      key={artist}
                      onClick={() => handleSelectArtist(artist)}
                      className={`w-full px-4 py-3 flex items-center gap-3 transition-colors text-left ${
                        isHighlighted ? "bg-primary/20" : "hover:bg-muted/50"
                      }`}
                    >
                      <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                        <Music className="w-4 h-4 text-secondary" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-foreground">{artist}</div>
                        <div className="text-xs text-muted-foreground">View all events</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventArtistSearch;
